
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';

interface OverdueTask {
  id: string;
  title: string;
  due_date: string;
  board_id: string;
  board_name: string;
  client_name: string;
  column_name: string;
}

export function useOverdueTasks() {
  const [tasks, setTasks] = useState<OverdueTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOverdueTasks = async () => {
    try {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get all overdue cards from user's boards
      const { data: cardsData, error } = await supabase
        .from('kanban_cards')
        .select(`
          id,
          title,
          due_date,
          is_completed,
          kanban_columns (
            id,
            name,
            kanban_boards (
              id,
              name,
              user_id,
              clients (
                name
              )
            )
          )
        `)
        .eq('is_archived', false)
        .not('due_date', 'is', null)
        .eq('is_completed', false);

      if (error) throw error;

      if (cardsData) {
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today

        const overdueTasks: OverdueTask[] = cardsData
          .filter(card => {
            // Filter only user's boards and overdue tasks
            const isUserBoard = card.kanban_columns?.kanban_boards?.user_id === user.id;
            const isOverdue = card.due_date && new Date(card.due_date) < today;
            return isUserBoard && isOverdue;
          })
          .map(card => ({
            id: card.id,
            title: card.title,
            due_date: card.due_date!,
            board_id: card.kanban_columns?.kanban_boards?.id || '',
            board_name: card.kanban_columns?.kanban_boards?.name || 'Quadro sem nome',
            client_name: card.kanban_columns?.kanban_boards?.clients?.name || 'Cliente não informado',
            column_name: card.kanban_columns?.name || 'Coluna sem nome'
          }))
          .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());

        setTasks(overdueTasks);
      }
    } catch (error: any) {
      console.error('Error fetching overdue tasks:', error);
      toast.error('Erro ao carregar tarefas em atraso');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOverdueTasks();
  }, []);

  return {
    tasks,
    isLoading,
    refreshTasks: fetchOverdueTasks
  };
}
